import {
  Button,
  Callout,
  FormLayout,
  GridFieldGroup,
  Heading,
  Link,
  MaskedInput,
  Paragraph,
  SubmitButton,
  TextInput,
} from '@expressable/syntax-ds';

import { zodResolver } from '@hookform/resolvers/zod';
import config from 'config';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { ResumeSignupLeadFormType, resumeSignupLeadSchema } from 'domains/signupDirect/ResumeLead/resumeSchema';
import { onSubmitLeadVerification } from 'domains/signupDirect/ResumeLead/submitVerificationStep';
import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from 'router/routes';

const DirectSignupResumeLeadStep = () => {
  const { onResumeFromLeadData } = useSignupDirectCtx();
  const [validationFailed, setValidationFailed] = useState(false);
  const { t } = useTranslation();

  const [params] = useSearchParams();
  const navigate = useNavigate();

  const leadID = params.get('leadID');

  if (!leadID) {
    return <Navigate to={routes.signupDirect.base} />;
  }

  const methods = useForm<ResumeSignupLeadFormType>({
    resolver: zodResolver(resumeSignupLeadSchema),
    defaultValues: {
      leadID,
    },
  });

  const { handleSubmit } = methods;

  const signupManualEmail = config.get('signupManualEmail') as string;

  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.resumeLead.form.title')}</Heading>
        <Paragraph size="lg" noDefaultMargins>
          {t('directSignup.resumeLead.form.subTitle')}
        </Paragraph>
      </div>

      <FormProvider {...methods}>
        <FormLayout
          className="mobile-xs-d:gap-e40 tablet-d:gap-e56"
          onSubmit={handleSubmit(async d => {
            const data = await onSubmitLeadVerification(d);

            if (!data) {
              setValidationFailed(true);
            } else {
              onResumeFromLeadData(data);
            }
          })}
        >
          <FormFields />

          {validationFailed && (
            <Callout type="warning">
              {t('directSignup.resumeLead.form.failedCallout')}{' '}
              <Link external={true} to={`mailto:${signupManualEmail}`}>
                {signupManualEmail}
              </Link>
            </Callout>
          )}

          <FormLayout.CTA>
            <SubmitButton data-testid="resume-lead-submit-button">
              {t('directSignup.resumeLead.form.submit')}
            </SubmitButton>
            {validationFailed && (
              <Button onClick={() => navigate(routes.signupDirect.base)} variant="link">
                {t('directSignup.resumeLead.form.startOver')}
              </Button>
            )}
          </FormLayout.CTA>
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields = () => {
  const { t } = useTranslation();
  const { register } = useFormContext<ResumeSignupLeadFormType>();

  return (
    <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
      <GridFieldGroup>
        <TextInput
          data-testid="client-first-name-input"
          label={t('directSignup.labels.clientFirstName')}
          {...register('firstName')}
        />
        <TextInput
          data-testid="client-last-name-input"
          label={t('directSignup.labels.clientLastName')}
          {...register('lastName')}
        />
        <MaskedInput
          data-testid="client-dob-input"
          label={t('directSignup.labels.dob')}
          {...register('dateOfBirth')}
          mask="MM/DD/YYYY"
          type="tel"
          showMask
          replacement={{
            M: /[0-1]|[0-9]/,
            D: /[0-3]|[0-9]/,
            Y: /\d/,
          }}
        />
      </GridFieldGroup>
    </FormLayout.Fields>
  );
};

export default DirectSignupResumeLeadStep;
